/**
 * Pulse Premium Subscription Selector
 * Enhanced subscription selector with smooth animations and UX improvements
 */

class PulseSubscriptionSelector extends HTMLElement {
  constructor() {
    super();

    this.originalPriceState = null;
    this.priceElement = null;
    this.priceContainer = null;
    this.sectionId = null;
    this.sellingPlansData = null;
    this.initialized = false;
    this.variantChangeUnsubscriber = null;

    this.handleRadioChange = this.handleRadioChange.bind(this);
    this.handleRadioClick = this.handleRadioClick.bind(this);
    this.handleFrequencyChange = this.handleFrequencyChange.bind(this);
    this.handleSubscriptionChanged = this.handleSubscriptionChanged.bind(this);
    this.handleThemeVariantChange = this.handleThemeVariantChange.bind(this);
    this.handleSubscriptionRadioSelected = this.handleSubscriptionRadioSelected.bind(this);
  }

  connectedCallback() {
    if (this.initialized) return;
    this.init();
    this.initialized = true;
  }

  disconnectedCallback() {
    this.removeEventListeners();
    this.initialized = false;
  }

  cacheDomReferences() {
    this.sellingPlanInput = this.querySelector('[data-selling-plan-input]');
    this.radioInputs = this.querySelectorAll('input[type="radio"][name^="purchase_option"]');
    this.frequencySelects = this.querySelectorAll('[data-frequency-select]');
    this.options = this.querySelectorAll('.pulse-subscription-selector__option');
  }

  removeEventListeners() {
    this.radioInputs?.forEach((input) => {
      input.removeEventListener('change', this.handleRadioChange);
      input.removeEventListener('click', this.handleRadioClick);
      input.removeEventListener('change', this.handleSubscriptionRadioSelected);
    });

    this.frequencySelects?.forEach((select) => {
      select.removeEventListener('change', this.handleFrequencyChange);
    });

    this.removeEventListener('subscription-changed', this.handleSubscriptionChanged);

    if (this.variantChangeUnsubscriber) {
      this.variantChangeUnsubscriber();
      this.variantChangeUnsubscriber = null;
    }
  }
  
  init() {
    this.cacheDomReferences();
    this.loadSellingPlansData();

    // Find the main price element
    this.sectionId = this.dataset.sectionId;
    if (this.sectionId) {
      const priceWrapper = document.getElementById(`price-${this.sectionId}`);
      if (priceWrapper) {
        this.priceContainer = priceWrapper.querySelector('.price');
        if (this.priceContainer) {
          // Store original price state
          this.storeOriginalPriceState();
        }
      }
    }
    
    // Listen to radio changes
    this.radioInputs.forEach(input => {
      input.addEventListener('change', this.handleRadioChange);
      input.addEventListener('click', this.handleRadioClick);
    });
    
    // Listen to frequency select changes
    this.frequencySelects.forEach(select => {
      select.addEventListener('change', this.handleFrequencyChange);
    });
    
    // Update frequency select when subscription is selected
    this.radioInputs.forEach(input => {
      if (input.value !== 'onetime') {
        input.addEventListener('change', this.handleSubscriptionRadioSelected);
      }
    });
    
    // Listen to subscription-changed events (including from frequency changes)
    this.addEventListener('subscription-changed', this.handleSubscriptionChanged);
    
    // Re-apply subscription price after theme variant updates replace the price HTML
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.handleThemeVariantChange);
    }

    // Run once after other deferred scripts finish their initial updates
    requestAnimationFrame(() => {
      this.syncSelectedPrice();
    });
    
    // Expose method globally for compatibility
    if (!window.getCurrentSellingPlanId) {
      window.getCurrentSellingPlanId = () => {
        return this.sellingPlanInput?.value || '';
      };
    }
  }

  handleSubscriptionRadioSelected(event) {
    if (!event.target.checked) return;

    const option = event.target.closest('.pulse-subscription-selector__option');
    const frequencySelect = option?.querySelector('[data-frequency-select]');
    if (frequencySelect) {
      this.updateSellingPlanFromFrequency(frequencySelect);
    }
  }

  handleThemeVariantChange(event) {
    const sectionId = event?.data?.sectionId;
    if (sectionId && sectionId !== this.sectionId) return;

    this.cacheDomReferences();
    this.loadSellingPlansData();
    this.refreshPriceContainer();

    requestAnimationFrame(() => {
      this.syncSelectedPrice();
    });
  }

  refreshPriceContainer() {
    if (!this.sectionId) return;

    const priceWrapper = document.getElementById(`price-${this.sectionId}`);
    if (!priceWrapper) return;

    this.priceContainer = priceWrapper.querySelector('.price');
    if (this.priceContainer) {
      this.storeOriginalPriceState();
    }
  }

  syncSelectedPrice() {
    if (!this.priceContainer) {
      this.refreshPriceContainer();
    }

    const checkedInput = this.querySelector('input[type="radio"]:checked');
    if (!checkedInput) return;

    if (checkedInput.value === 'subscription') {
      const frequencySelect = checkedInput.closest('.pulse-subscription-selector__option')?.querySelector('[data-frequency-select]');
      if (frequencySelect) {
        this.updateSellingPlanFromFrequency(frequencySelect);
      } else {
        this.updateSellingPlan(checkedInput);
      }
      this.updateMainPrice(checkedInput);
    } else {
      this.updateSellingPlan(checkedInput);
    }
  }
  
  loadSellingPlansData() {
    const script = this.querySelector('[data-pulse-selling-plans-json]');
    if (!script) return;

    try {
      this.sellingPlansData = JSON.parse(script.textContent);
    } catch (error) {
      console.warn('Could not parse selling plans data:', error);
      this.sellingPlansData = null;
    }
  }

  getActiveSellingPlanId() {
    const checkedInput = this.querySelector('input[type="radio"]:checked');
    if (!checkedInput || checkedInput.value !== 'subscription') return null;

    const option = checkedInput.closest('.pulse-subscription-selector__option');
    const frequencySelect = option?.querySelector('[data-frequency-select]');
    if (frequencySelect) {
      return frequencySelect.value;
    }

    return checkedInput.dataset.sellingPlanValue || null;
  }

  getPlanPricing(sellingPlanId, select = null) {
    if (!sellingPlanId) return null;

    const variantId = this.getCurrentVariantId();
    if (this.sellingPlansData?.variants && variantId) {
      const variantData = this.sellingPlansData.variants[String(variantId)];
      const planData = variantData?.plans?.[String(sellingPlanId)];
      if (planData) {
        return {
          price: planData.price,
          compareAtPrice: planData.compare_at_price,
          oneTimePrice: variantData.one_time_price
        };
      }
    }

    if (select) {
      const selectedOption = select.options[select.selectedIndex];
      if (selectedOption) {
        const price = parseInt(selectedOption.dataset.subscriptionPrice || '0', 10);
        const compareAtPrice = parseInt(selectedOption.dataset.oneTimePrice || '0', 10);
        if (price > 0) {
          return {
            price,
            compareAtPrice,
            oneTimePrice: compareAtPrice
          };
        }
      }
    }

    return null;
  }

  formatMoneyFromCents(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, '{{amount}}');
    }
    return this.formatPrice(cents);
  }

  updateSubscriptionCardDisplay(option, pricing) {
    if (!option || !pricing) return;

    const oneTimePrice = pricing.compareAtPrice || pricing.oneTimePrice;
    const savingsAmount = oneTimePrice - pricing.price;

    option.dataset.subscriptionPrice = String(pricing.price);
    option.dataset.oneTimePrice = String(oneTimePrice);

    const priceEl = option.querySelector('.pulse-subscription-selector__price-amount');
    if (priceEl) {
      priceEl.textContent = this.formatMoneyFromCents(pricing.price);
    }

    const savingsContainer = option.querySelector('.pulse-subscription-selector__savings');
    if (savingsContainer && savingsAmount > 0) {
      const savingsText = option.dataset.savingsAmountText || 'You save';
      const savingsPeriod = option.dataset.savingsPeriodText || 'per delivery';
      const textSpan = savingsContainer.querySelector('span:last-child');
      if (textSpan) {
        textSpan.textContent = `${savingsText} ${this.formatMoneyFromCents(savingsAmount)} ${savingsPeriod}`;
      }
    }

    const savingsBadge = option.querySelector('.pulse-subscription-selector__savings-badge');
    if (savingsBadge && oneTimePrice > 0 && savingsAmount > 0) {
      const savingsPercentage = Math.round((savingsAmount * 100) / oneTimePrice);
      const savingsLabel = savingsBadge.textContent.split(/\d+/)[0].trim() || 'Save';
      savingsBadge.textContent = `${savingsLabel} ${savingsPercentage}%`;
    }
  }

  applyPlanPricing(pricing, option = null) {
    if (!pricing || !this.priceContainer) return;

    const oneTimePrice = pricing.compareAtPrice || pricing.oneTimePrice;
    const subscriptionPrice = this.formatMoneyFromCents(pricing.price);
    const oneTimePriceFormatted = this.formatMoneyFromCents(oneTimePrice);

    if (option) {
      this.updateSubscriptionCardDisplay(option, pricing);
    }

    this.applyPriceUpdate(
      subscriptionPrice,
      oneTimePriceFormatted,
      pricing.price < oneTimePrice
    );
  }

  storeOriginalPriceState() {
    if (!this.priceContainer) return;
    
    const regularPriceEl = this.priceContainer.querySelector('.price__regular .price-item--regular');
    const salePriceEl = this.priceContainer.querySelector('.price__sale .price-item--sale');
    const comparePriceEl = this.priceContainer.querySelector('.price__sale .price-item--regular');
    
    this.originalPriceState = {
      hasSale: this.priceContainer.classList.contains('price--on-sale'),
      regularPrice: regularPriceEl ? regularPriceEl.textContent.trim() : null,
      salePrice: salePriceEl ? salePriceEl.textContent.trim() : null,
      comparePrice: comparePriceEl ? comparePriceEl.textContent.trim() : null,
      regularPriceHTML: regularPriceEl ? regularPriceEl.outerHTML : null,
      salePriceHTML: salePriceEl ? salePriceEl.outerHTML : null,
      comparePriceHTML: comparePriceEl ? comparePriceEl.outerHTML : null,
      containerHTML: this.priceContainer.innerHTML
    };
  }
  
  handleRadioChange(e) {
    this.updateSellingPlan(e.target);
    this.animateSelection(e.target);
    this.updateMainPrice(e.target);
  }
  
  handleRadioClick(e) {
    // Prevent double-triggering on mobile
    if (e.target.checked) {
      this.animateSelection(e.target);
    }
  }
  
  handleFrequencyChange(e) {
    this.updateSellingPlanFromFrequency(e.target);
    // Update price when frequency changes
    const checkedInput = this.querySelector('input[type="radio"]:checked');
    if (checkedInput && checkedInput.value === 'subscription') {
      // Fetch updated price for the new selling plan
      this.updateMainPriceFromFrequency(e.target);
    }
  }
  
  handleSubscriptionChanged() {
    // Subscription price updates are handled by handleRadioChange and handleFrequencyChange.
    // Only restore the main price when switching back to one-time purchase.
    const checkedInput = this.querySelector('input[type="radio"]:checked');
    if (checkedInput && checkedInput.value === 'onetime') {
      this.restoreOriginalPrice();
    }
  }
  
  updateMainPriceFromFrequency(select) {
    if (!this.priceContainer) return;

    const sellingPlanId = select.value;
    const option = select.closest('.pulse-subscription-selector__option');
    const pricing = this.getPlanPricing(sellingPlanId, select);

    if (pricing) {
      this.applyPlanPricing(pricing, option);
      return;
    }

    const checkedInput = this.querySelector('input[type="radio"]:checked');
    if (checkedInput) {
      this.updateMainPrice(checkedInput);
    }
  }
  
  updateMainPrice(input) {
    if (!this.priceContainer) return;
    
    const option = input.closest('.pulse-subscription-selector__option');
    if (!option) return;
    
    if (input.value === 'subscription') {
      const frequencySelect = option.querySelector('[data-frequency-select]');
      if (frequencySelect) {
        this.updateMainPriceFromFrequency(frequencySelect);
        return;
      }

      const sellingPlanId = input.dataset.sellingPlanValue;
      const pricing = this.getPlanPricing(sellingPlanId);

      if (pricing) {
        this.applyPlanPricing(pricing, option);
        return;
      }

      const subscriptionPriceEl = option.querySelector('.pulse-subscription-selector__price-amount');
      if (!subscriptionPriceEl) return;
      
      const subscriptionPrice = subscriptionPriceEl.textContent.trim();
      const subscriptionPriceValue = parseInt(option.dataset.subscriptionPrice || '0', 10);
      const oneTimePriceValue = parseInt(option.dataset.oneTimePrice || '0', 10);
      
      const oneTimeOption = this.querySelector('.pulse-subscription-selector__option--onetime');
      const oneTimePrice = oneTimeOption ? 
        (oneTimeOption.querySelector('.pulse-subscription-selector__price-amount')?.textContent.trim() || 
         this.formatPrice(oneTimePriceValue)) : 
        this.getOneTimePrice();
      
      this.applyPriceUpdate(subscriptionPrice, oneTimePrice, subscriptionPriceValue < oneTimePriceValue);
    } else if (input.value === 'onetime') {
      this.restoreOriginalPrice();
    }
  }
  
  clearPriceDisplayOverrides() {
    if (!this.priceContainer) return;

    const priceRegularContainer = this.priceContainer.querySelector('.price__regular');
    const priceSaleContainer = this.priceContainer.querySelector('.price__sale');
    if (priceRegularContainer) priceRegularContainer.style.removeProperty('display');
    if (priceSaleContainer) priceSaleContainer.style.removeProperty('display');
  }

  applyPriceUpdate(subscriptionPrice, oneTimePrice, showComparePrice = true) {
    if (!this.priceContainer) return;

    this.clearPriceDisplayOverrides();
    
    const regularPriceEl = this.priceContainer.querySelector('.price__regular .price-item--regular');
    const salePriceEl = this.priceContainer.querySelector('.price__sale .price-item--sale');
    const comparePriceEl = this.priceContainer.querySelector('.price__sale .price-item--regular');
    const priceRegularContainer = this.priceContainer.querySelector('.price__regular');
    let priceSaleContainer = this.priceContainer.querySelector('.price__sale');
    
    // If subscription price is lower than one-time, show as sale
    if (showComparePrice && subscriptionPrice !== oneTimePrice) {
      // Create sale container if it doesn't exist
      if (!priceSaleContainer) {
        priceSaleContainer = document.createElement('div');
        priceSaleContainer.className = 'price__sale';
        this.priceContainer.querySelector('.price__container').appendChild(priceSaleContainer);
      }
      
      // Update or create compare price (one-time price, strikethrough)
      if (comparePriceEl) {
        // Update existing compare price
        const sTag = comparePriceEl.querySelector('s') || comparePriceEl;
        if (sTag.tagName === 'S') {
          sTag.textContent = oneTimePrice;
        } else {
          comparePriceEl.innerHTML = `<s>${oneTimePrice}</s>`;
        }
      } else {
        // Create compare price element
        const comparePriceSpan = document.createElement('span');
        comparePriceSpan.className = 'price-item price-item--regular';
        const s = document.createElement('s');
        s.textContent = oneTimePrice;
        comparePriceSpan.appendChild(s);
        // Insert before sale price
        if (salePriceEl) {
          priceSaleContainer.insertBefore(comparePriceSpan, salePriceEl);
        } else {
          priceSaleContainer.appendChild(comparePriceSpan);
        }
      }
      
      // Update or create sale price (subscription price)
      if (salePriceEl) {
        salePriceEl.textContent = subscriptionPrice;
      } else {
        const salePriceSpan = document.createElement('span');
        salePriceSpan.className = 'price-item price-item--sale price-item--last';
        salePriceSpan.textContent = subscriptionPrice;
        priceSaleContainer.appendChild(salePriceSpan);
      }
      
      // Add sale class
      this.priceContainer.classList.add('price--on-sale');
    } else {
      // Update regular price
      if (regularPriceEl) {
        regularPriceEl.textContent = subscriptionPrice;
      } else if (priceRegularContainer) {
        const regularPriceSpan = document.createElement('span');
        regularPriceSpan.className = 'price-item price-item--regular';
        regularPriceSpan.textContent = subscriptionPrice;
        priceRegularContainer.appendChild(regularPriceSpan);
      }
      
      // Remove sale class
      this.priceContainer.classList.remove('price--on-sale');
    }
    
    // Ensure price wrapper is visible
    const priceWrapper = this.priceContainer.closest('.price-wrapper');
    if (priceWrapper) {
      priceWrapper.classList.remove('visibility-hidden');
    }
  }
  
  restoreOriginalPrice() {
    if (!this.priceContainer || !this.originalPriceState) return;
    
    // Restore original HTML structure
    this.priceContainer.innerHTML = this.originalPriceState.containerHTML;
    this.clearPriceDisplayOverrides();
    
    // Restore sale class state
    if (this.originalPriceState.hasSale) {
      this.priceContainer.classList.add('price--on-sale');
    } else {
      this.priceContainer.classList.remove('price--on-sale');
    }
    
    // Ensure price wrapper is visible
    const priceWrapper = this.priceContainer.closest('.price-wrapper');
    if (priceWrapper) {
      priceWrapper.classList.remove('visibility-hidden');
    }
  }
  
  getOneTimePrice() {
    const oneTimeOption = this.querySelector('.pulse-subscription-selector__option--onetime');
    if (oneTimeOption) {
      const priceEl = oneTimeOption.querySelector('.pulse-subscription-selector__price-amount');
      if (priceEl) {
        return priceEl.textContent.trim();
      }
      // Fallback to data attribute
      const priceValue = parseInt(oneTimeOption.dataset.oneTimePrice || '0', 10);
      if (priceValue > 0) {
        return this.formatPrice(priceValue);
      }
    }
    // Last resort: use original price state
    if (this.originalPriceState) {
      return this.originalPriceState.regularPrice || this.originalPriceState.salePrice;
    }
    return '';
  }
  
  formatPrice(priceInCents) {
    // Basic price formatting - Shopify should handle this, but fallback if needed
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD'
    }).format(priceInCents / 100);
  }
  
  getCurrentVariantId() {
    const variantInput = document.querySelector('input[name="id"]');
    return variantInput ? variantInput.value : null;
  }
  
  getProductHandle() {
    const productForm = document.querySelector('form[action*="/cart/add"]');
    if (productForm) {
      const action = productForm.getAttribute('action');
      const match = action.match(/\/products\/([^\/]+)/);
      if (match) return match[1];
    }
    // Fallback: try to get from URL
    const urlMatch = window.location.pathname.match(/\/products\/([^\/]+)/);
    return urlMatch ? urlMatch[1] : '';
  }
  
  updateSellingPlan(input) {
    if (!this.sellingPlanInput) return;
    
    const sellingPlanValue = input.dataset.sellingPlanValue || '';
    this.sellingPlanInput.value = sellingPlanValue;
    
    // Dispatch custom event for other components
    this.dispatchEvent(new CustomEvent('subscription-changed', {
      detail: {
        sellingPlanId: sellingPlanValue,
        optionType: input.value
      },
      bubbles: true
    }));
  }
  
  updateSellingPlanFromFrequency(select) {
    if (!this.sellingPlanInput) return;
    
    const sellingPlanId = select.value;
    this.sellingPlanInput.value = sellingPlanId;
    
    // Update the radio button's data attribute
    const option = select.closest('.pulse-subscription-selector__option');
    const radioInput = option?.querySelector('input[type="radio"]');
    if (radioInput) {
      radioInput.dataset.sellingPlanValue = sellingPlanId;
    }
    
    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('subscription-changed', {
      detail: {
        sellingPlanId: sellingPlanId,
        optionType: 'subscription'
      },
      bubbles: true
    }));
  }
  
  getCurrentSellingPlanId() {
    return this.sellingPlanInput?.value || '';
  }
  
  // Public method to programmatically set subscription
  setSubscription(sellingPlanId) {
    const input = this.querySelector(`input[data-selling-plan-value="${sellingPlanId}"]`);
    if (input) {
      input.checked = true;
      this.updateSellingPlan(input);
      this.animateSelection(input);
      this.updateMainPrice(input);
    }
  }
  
  // Public method to set one-time purchase
  setOneTime() {
    const input = this.querySelector('input[value="onetime"]');
    if (input) {
      input.checked = true;
      this.updateSellingPlan(input);
      this.animateSelection(input);
      this.updateMainPrice(input);
    }
  }
  
  animateSelection(input) {
    const option = input.closest('.pulse-subscription-selector__option');
    if (!option) return;
    
    const card = option.querySelector('.pulse-subscription-selector__card');
    if (!card) return;
    
    // Add a subtle animation class
    card.classList.add('pulse-subscription-selector__card--selected');
    
    // Remove animation class after animation completes
    setTimeout(() => {
      card.classList.remove('pulse-subscription-selector__card--selected');
    }, 300);
  }
}

// Define custom element
if (!customElements.get('pulse-subscription-selector')) {
  customElements.define('pulse-subscription-selector', PulseSubscriptionSelector);
}

// Re-initialize when Shopify theme editor reloads a section
document.addEventListener('shopify:section:load', (event) => {
  const section = event.target;
  if (!section) return;

  section.querySelectorAll('pulse-subscription-selector').forEach((selector) => {
    if (selector.initialized) {
      selector.disconnectedCallback();
      selector.connectedCallback();
    }
  });
});

// Export for global access
window.PulseSubscriptionSelector = PulseSubscriptionSelector;

// Compatibility with existing code
// This function will work with both the new premium selector and the old selling-plans snippet
window.getCurrentSellingPlanId = function() {
  // Try new premium selector first
  const premiumSelector = document.querySelector('pulse-subscription-selector');
  if (premiumSelector) {
    return premiumSelector.getCurrentSellingPlanId();
  }
  
  // Fallback to old selling-plans snippet
  const oldSelector = document.querySelector('product-subscriptions');
  if (oldSelector && oldSelector.getCurrentSellingPlanId) {
    return oldSelector.getCurrentSellingPlanId();
  }
  
  // Last resort: check hidden input directly
  const hiddenInput = document.querySelector('input[name="selling_plan"]');
  return hiddenInput ? hiddenInput.value : '';
};

